'use client';
import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { capturePageview, initAnalytics, registerSuperProperties } from '@/lib/analytics/client';
import type { Surface } from '@/lib/analytics/events';

/** The dashboards are split by area, so every event carries where it happened. */
function surfaceFor(pathname: string): Surface {
  if (pathname.startsWith('/app/professional')) return 'pro';
  if (pathname.startsWith('/app/patient')) return 'patient';
  if (pathname.startsWith('/app/admin')) return 'admin';
  return 'site';
}

/**
 * Pageviews for the App Router. Client navigation never reloads the document,
 * so the library's own capture would fire once per hard load and miss every
 * route change after it — which would make every funnel step past the first
 * look empty.
 *
 * Reads useSearchParams, hence the Suspense boundary in the exported wrapper:
 * without it this de-opts every statically rendered page to client rendering.
 */
function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;
    registerSuperProperties({
      surface: surfaceFor(pathname),
      app_version: process.env.NEXT_PUBLIC_APP_VERSION,
    });
    const query = searchParams?.toString();
    // The URL is scrubbed inside capturePageview — never pass it on raw.
    capturePageview(`${window.location.origin}${pathname}${query ? `?${query}` : ''}`);
  }, [pathname, searchParams]);

  return null;
}

/**
 * App-wide analytics boot. Mounted once in the root layout so the PUBLIC site
 * is instrumented too: the patient booking funnel (F3) starts on a marketing
 * page long before anyone logs in.
 */
export function AnalyticsProvider() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Suspense fallback={null}>
      <PageviewTracker />
    </Suspense>
  );
}
